import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Globe, MapPin, X } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { BUSINESS } from "../data/siteConfig";
import { WhatsAppIcon } from "./WhatsAppIcon";

const Footer = () => {
  const { t } = useLanguage();
  const [policy, setPolicy] = useState(null);
  const activePolicy = policy ? t.footer.policies[policy] : null;

  return (
    <footer data-testid="site-footer" className="bg-night text-ivory">
      <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-20">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <p className="font-head text-2xl">{t.brand.name}</p>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-ivory/60">
              {t.footer.blurb}
            </p>
          </div>
          <div className="md:col-span-4">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-bronze-light">
              {t.footer.contactHeading}
            </p>
            <ul className="mt-5 flex flex-col gap-3 text-sm">
              <li>
                <a
                  href={`tel:+${BUSINESS.whatsappNumber}`}
                  data-testid="footer-phone-link"
                  className="inline-flex items-center gap-2.5 text-ivory/80 transition-colors duration-300 hover:text-bronze-light"
                >
                  <WhatsAppIcon className="h-4 w-4" />
                  <span dir="ltr">{BUSINESS.phoneDisplay}</span>
                </a>
              </li>
              <li>
                <a
                  href={BUSINESS.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-website-link"
                  className="inline-flex items-center gap-2.5 text-ivory/80 transition-colors duration-300 hover:text-bronze-light"
                >
                  <Globe className="h-4 w-4" />
                  www.hayabeautycare.com
                </a>
              </li>
              <li className="inline-flex items-center gap-2.5 text-ivory/80">
                <MapPin className="h-4 w-4" />
                {t.footer.location}
              </li>
            </ul>
          </div>
          <div className="md:col-span-3">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-bronze-light">
              {t.footer.legalHeading}
            </p>
            <ul className="mt-5 flex flex-col gap-3 text-sm">
              <li>
                <button
                  type="button"
                  onClick={() => setPolicy("privacy")}
                  data-testid="footer-privacy-link"
                  className="text-ivory/80 transition-colors duration-300 hover:text-bronze-light"
                >
                  {t.footer.privacy}
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => setPolicy("cookies")}
                  data-testid="footer-cookies-link"
                  className="text-ivory/80 transition-colors duration-300 hover:text-bronze-light"
                >
                  {t.footer.cookies}
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 border-t border-ivory/10 pt-8">
          <p className="text-xs text-ivory/45">
            © {new Date().getFullYear()} {BUSINESS.name}. {t.footer.rights}
          </p>
        </div>
      </div>

      <AnimatePresence>
        {activePolicy && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/60 p-6 backdrop-blur-sm"
            onClick={() => setPolicy(null)}
            role="dialog"
            aria-modal="true"
            aria-label={activePolicy.title}
          >
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 30, scale: 0.97 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              data-testid="policy-modal"
              className="w-full max-w-lg rounded-2xl bg-ivory p-8 text-ink shadow-2xl md:p-10"
            >
              <div className="flex items-start justify-between gap-6">
                <h3 className="font-head text-2xl">{activePolicy.title}</h3>
                <button
                  type="button"
                  onClick={() => setPolicy(null)}
                  data-testid="policy-modal-close"
                  aria-label="Close"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-line text-ink transition-colors duration-300 hover:border-bronze hover:text-bronze-dark"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <p className="mt-5 text-sm leading-relaxed text-taupe">{activePolicy.body}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </footer>
  );
};

export default Footer;
